<template>
  <div class="itr" role="dialog" aria-modal="true" :aria-label="$trf('Tour, step {0} of {1}', [i + 1, shown.length])">
    <div class="itr-catch" @click="finish"></div>
    <div class="itr-spot" :style="spotStyle" aria-hidden="true"><span class="itr-ring"></span></div>
    <div v-if="step" class="itr-card" :style="cardStyle" :key="i">
      <div class="itr-head">
        <span class="itr-badge"><InvIcon name="compass" /></span>
        <span class="itr-count">{{ i + 1 }} of {{ shown.length }}</span>
        <button type="button" class="itr-skip" @click="finish">Skip</button>
      </div>
      <p class="itr-title">{{ step.title }}</p>
      <p class="itr-text">{{ step.text }}</p>
      <div class="itr-foot">
        <span class="itr-dots" aria-hidden="true"><i v-for="(s, n) in shown" :key="n" :class="{ on: n === i, done: n < i }"></i></span>
        <button v-if="i > 0" type="button" class="inv-btn is-small is-ghost" @click="go(i - 1)">Back</button>
        <button type="button" class="inv-btn is-small is-primary" @click="next">{{ i === shown.length - 1 ? $tr("Got it") : $tr("Next") }}<InvIcon v-if="i < shown.length - 1" name="arrow-right" /></button>
      </div>
    </div>
  </div>
</template>

<script>
import InvIcon from "./InvIcon.vue";

const PAD = 6;
const CARD_W = 320;

/**
 * The first-visit walk around the workspace: a spotlight on one part of the screen at a time ([data-tour="…"]) and a
 * card that says what it is for. Parts not on screen (a narrow window hides some) are left out. Esc skips; the
 * arrow keys move.
 */
export default {
  name: "InvTour",
  components: { InvIcon },
  props: { steps: { type: Array, required: true } },
  emits: ["done"],
  data() {
    return { i: 0, rect: null, shown: [] };
  },
  computed: {
    step() {
      return this.shown[this.i] || null;
    },
    spotStyle() {
      const r = this.rect;
      if (!r) return { opacity: 0 };
      return { top: r.top - PAD + "px", left: r.left - PAD + "px", width: r.width + PAD * 2 + "px", height: r.height + PAD * 2 + "px" };
    },
    cardStyle() {
      const r = this.rect;
      if (!r) return { top: "30vh", left: "calc(50% - " + CARD_W / 2 + "px)" };
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = Math.max(12, Math.min(vw - CARD_W - 12, r.left + r.width / 2 - CARD_W / 2));
      const below = r.bottom + PAD + 14;
      // Under the part when there is room, else above it.
      if (below + 220 < vh) return { top: below + "px", left: left + "px" };
      return { top: Math.max(12, r.top - PAD - 14 - 220) + "px", left: left + "px" };
    },
  },
  mounted() {
    this.shown = this.steps.filter((s) => this.target(s));
    if (!this.shown.length) {
      this.$emit("done");
      return;
    }
    this._onKey = (e) => {
      if (e.key === "Escape") this.finish();
      else if (e.key === "ArrowRight") this.next();
      else if (e.key === "ArrowLeft" && this.i > 0) this.go(this.i - 1);
    };
    this._onResize = () => this.measure();
    window.addEventListener("keydown", this._onKey);
    window.addEventListener("resize", this._onResize);
    this.measure();
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this._onKey);
    window.removeEventListener("resize", this._onResize);
  },
  methods: {
    target(s) {
      const el = document.querySelector('[data-tour="' + s.target + '"]');
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 ? el : null;
    },
    measure() {
      const el = this.step && this.target(this.step);
      this.rect = el ? el.getBoundingClientRect() : null;
    },
    go(n) {
      this.i = n;
      this.$nextTick(() => this.measure());
    },
    next() {
      if (this.i < this.shown.length - 1) this.go(this.i + 1);
      else this.finish();
    },
    finish() {
      this.$emit("done");
    },
  },
};
</script>

<style scoped>
.itr { position: fixed; inset: 0; z-index: 1400; }
.itr-catch { position: absolute; inset: 0; }
.itr-spot {
  position: fixed; border-radius: var(--r-lg); pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(8, 10, 16, 0.62);
  transition: top var(--iw-base) var(--iw-ease), left var(--iw-base) var(--iw-ease), width var(--iw-base) var(--iw-ease), height var(--iw-base) var(--iw-ease), opacity var(--iw-fast) linear;
}
.itr-ring { position: absolute; inset: -2px; border-radius: inherit; border: 2px solid var(--accent-primary); animation: itr-pulse 1.8s var(--iw-ease) infinite; }
@keyframes itr-pulse {
  0% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.45); }
  70% { box-shadow: 0 0 0 10px rgba(249, 115, 22, 0); }
  100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0); }
}
.itr-card {
  box-sizing: border-box; position: fixed; width: 320px; padding: var(--s-4); border-radius: var(--r-lg);
  background: var(--bg-modal, var(--bg-primary)); border: 1px solid var(--border-primary); box-shadow: var(--shadow-overlay);
  animation: iw-pop var(--iw-base) var(--iw-spring) both;
  transition: top var(--iw-base) var(--iw-ease), left var(--iw-base) var(--iw-ease);
}
.itr-head { display: flex; align-items: center; gap: var(--s-2); margin-bottom: var(--s-3); }
.itr-badge { width: 24px; height: 24px; border-radius: var(--r-md); display: inline-flex; align-items: center; justify-content: center; background: var(--gradient-accent); color: #fff; }
.itr-badge .inv-icon { width: 14px; height: 14px; }
.itr-count { font-size: var(--t-caption); font-weight: var(--w-semibold); color: var(--text-muted); letter-spacing: var(--track-wide); }
.itr-skip { margin-left: auto; border: 0; background: transparent; color: var(--text-muted); font-family: inherit; font-size: var(--t-caption); cursor: pointer; padding: 2px 4px; border-radius: var(--r-sm); }
.itr-skip:hover { color: var(--text-primary); }
.itr-title { margin: 0 0 var(--s-1); font-size: var(--t-heading-sm); line-height: var(--t-heading-sm-lh); font-weight: var(--w-semibold); color: var(--text-primary); }
.itr-text { margin: 0; font-size: var(--t-body); line-height: var(--t-body-lh); color: var(--text-secondary); }
.itr-foot { display: flex; align-items: center; gap: var(--s-2); margin-top: var(--s-4); }
.itr-dots { display: inline-flex; gap: 5px; margin-right: auto; }
.itr-dots i { width: 6px; height: 6px; border-radius: 999px; background: var(--border-secondary); transition: width var(--iw-base) var(--iw-ease), background var(--iw-base) linear; }
.itr-dots i.done { background: color-mix(in srgb, var(--accent-primary) 50%, transparent); }
.itr-dots i.on { width: 18px; background: var(--accent-primary); }
</style>
